import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  UseDisclosureReturn,
  VStack,
} from "@chakra-ui/react";
import {
  EmrBed,
  EmrCheck,
  EmrNurse,
  EmrVitals,
} from "@medstonetech/slate-icons";

import {
  EncounterUserType,
  UserType,
  useUsers,
  UseUsersResponseItem,
} from "modules/identity";
import { PersonalInfo } from "modules/in-process/components/in-process-patients/PersonalInfo";
import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, SearchBar } from "shared";
import { debounce } from "throttle-debounce";
import { ChartType } from "types";

type AddTransferCareProviderModalProps = Pick<
  UseDisclosureReturn,
  "isOpen" | "onClose"
> & {
  progressNoteId: string;
  chartType: ChartType;
  onSelectedProvider: (
    provider: UseUsersResponseItem,
    providerType: EncounterUserType
  ) => void;
};

function AddTransferCareProviderModal(
  props: AddTransferCareProviderModalProps
) {
  const { onClose, isOpen, chartType, onSelectedProvider } = props;

  const [selectedOption, setSelectedOption] = useState<string>("ER");
  const [chartTeam, setChartTeam] = useState<UserType>(
    chartType === "Triage" ? "Nurse" : "Provider"
  );

  const [search, setSearch] = useState("");
  const [selectedProvider, setSelectedProvider] =
    useState<UseUsersResponseItem | null>(null);

  const { data, refetch } = useUsers({
    userType: chartTeam,
    search,
  });

  const onSearch = useMemo(
    () =>
      debounce<React.ChangeEventHandler<HTMLInputElement>>(1000, (e) => {
        setSearch(e.target.value);
      }),
    []
  );

  const handleClose = () => {
    setSelectedProvider(null);
    onClose();
  };

  const handleSelectProvider = () => {
    if (selectedProvider) {
      const getRole = (): EncounterUserType | undefined => {
        if (chartType === "Triage") {
          return selectedOption === "ER" ? "Nurse" : "ObservationNurse";
        } else if (chartType === "Chart") {
          if (chartTeam === "Provider") {
            return selectedOption === "ER" ? "Provider" : "ObservationProvider";
          } else if (chartTeam === "SupervisingProvider") {
            return selectedOption === "ER"
              ? "Supervisor"
              : "ObservationSupervisor";
          }
        }
      };

      const selectedProviderType = getRole();
      if (selectedProviderType)
        onSelectedProvider(selectedProvider, selectedProviderType);
    }
    onClose();
  };

  useEffect(() => {
    refetch();
  }, [search, refetch]);

  const ProviderList = useCallback(
    () => (
      <Flex flexDir={"column"} width="100%" height="100%" paddingX={3}>
        <SearchBar onChange={onSearch} />
        <Flex flexDir={"column"} gap={4} mt={4}>
          {data?.data.map((item) => (
            <Card
              padding="15px 21px"
              key={item.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              cursor="pointer"
              border="unset"
              boxShadow="none"
              _selected={{ bg: "blue", color: "white" }}
              onClick={() => setSelectedProvider(item)}
            >
              <PersonalInfo
                name={item.fullName}
                pictureUrl={item.pictureUrl}
                description={item.teams}
              />
              {item.id === selectedProvider?.id && (
                <Icon as={EmrCheck} fontSize="1rem" color="blue" />
              )}
            </Card>
          ))}
        </Flex>
      </Flex>
    ),
    [data?.data, onSearch, selectedProvider?.id]
  );

  const buttonStyle = (isSelected: boolean) => ({
    gap: 3,
    width: 275,
    height: "50px",
    borderRadius: ".5rem",
    color: isSelected ? "white" : "gray.600",
    background: isSelected ? "purple" : "white",
    border: isSelected ? "none" : "1px solid gray",
    _hover: {
      color: isSelected ? "white" : "gray.600",
      background: isSelected ? "purple" : "white",
      border: isSelected ? "none" : "1px solid gray",
    },
    _active: {
      color: isSelected ? "white" : "gray.600",
      background: isSelected ? "purple" : "white",
      border: isSelected ? "none" : "1px solid gray",
    },
    _focus: {
      boxShadow: isSelected ? "0 0 0 3px rgba(128, 90, 213, 0.6)" : "none",
    },
  });

  const getIconColor = (isSelected: boolean) =>
    isSelected ? "white" : "gray.600";

  const renderButtons = () => {
    if (chartType === "Triage") {
      return (
        <Flex justify="center" mb={4}>
          <HStack spacing={8}>
            <Button
              onClick={() => setSelectedOption("ER")}
              colorScheme="purple"
              sx={buttonStyle(selectedOption === "ER")}
            >
              <Icon
                as={EmrNurse}
                fontSize="1.25rem"
                color={getIconColor(selectedOption === "ER")}
              />
              ER Nurse
            </Button>
            <Button
              onClick={() => setSelectedOption("Observation")}
              colorScheme="purple"
              sx={buttonStyle(selectedOption === "Observation")}
            >
              <Icon
                as={EmrBed}
                fontSize="1.25rem"
                color={getIconColor(selectedOption === "Observation")}
              />
              Observation Nurse
            </Button>
          </HStack>
        </Flex>
      );
    }

    return (
      <VStack spacing={4} mb={4} align="center">
        <HStack spacing={8}>
          <Button
            gap={3}
            width={250}
            borderRadius=".5rem"
            variant="ghost"
            onClick={() => {
              setSelectedOption("ER");
              setChartTeam("Provider");
            }}
            colorScheme="purple"
            sx={buttonStyle(
              selectedOption === "ER" && chartTeam === "Provider"
            )}
          >
            <Icon
              as={EmrVitals}
              fontSize="1.25rem"
              color={getIconColor(
                selectedOption === "ER" && chartTeam === "Provider"
              )}
            />
            ER Provider
          </Button>
          <Button
            gap={3}
            width={250}
            borderRadius=".5rem"
            variant="ghost"
            onClick={() => {
              setSelectedOption("Observation");
              setChartTeam("Provider");
            }}
            colorScheme="purple"
            sx={buttonStyle(
              selectedOption === "Observation" && chartTeam === "Provider"
            )}
          >
            <Icon
              as={EmrBed}
              fontSize="1.25rem"
              color={getIconColor(
                selectedOption === "Observation" && chartTeam === "Provider"
              )}
            />
            Observation Provider
          </Button>
        </HStack>
        <HStack spacing={8}>
          <Button
            gap={3}
            width={250}
            borderRadius=".5rem"
            variant="ghost"
            onClick={() => {
              setSelectedOption("Observation");
              setChartTeam("SupervisingProvider");
            }}
            colorScheme="purple"
            sx={buttonStyle(
              selectedOption === "Observation" &&
                chartTeam === "SupervisingProvider"
            )}
          >
            <Icon
              as={EmrVitals}
              fontSize="1.25rem"
              color={getIconColor(
                selectedOption === "Observation" &&
                  chartTeam === "SupervisingProvider"
              )}
            />
            ER Supervisor
          </Button>
          <Button
            gap={3}
            width={250}
            borderRadius=".5rem"
            variant="ghost"
            onClick={() => setSelectedOption("Observation Supervisor")}
            colorScheme="purple"
            sx={buttonStyle(selectedOption === "Observation Supervisor")}
          >
            <Icon
              as={EmrBed}
              fontSize="1.25rem"
              color={getIconColor(selectedOption === "Observation Supervisor")}
            />
            Observation Supervisor
          </Button>
        </HStack>
      </VStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        bg="gray.200"
        maxW="unset"
        width="640px"
        containerProps={{ justifyContent: "center", alignItems: "center" }}
        height="78%"
        margin="0"
        overflowY="auto"
        overflowX="hidden"
        mr="10px"
      >
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          fontSize="1rem"
          padding="20px 16px 30px"
        >
          <Button variant="label" onClick={handleClose} color="blue">
            Cancel
          </Button>
          <Heading fontWeight="600" fontSize="1.0625rem">
            {chartType === "Chart" ? "Providers" : "Nurses"}
          </Heading>
          <Button
            variant="label"
            onClick={handleSelectProvider}
            color="blue"
            disabled={!selectedProvider}
          >
            Done
          </Button>
        </ModalHeader>
        <ModalBody
          padding="34px 15px 15px"
          display="flex"
          flexDirection="column"
          h="100%"
        >
          {renderButtons()}
          <Box width="100%" height="100%">
            {ProviderList()}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export { AddTransferCareProviderModal };
